import { TLShape, Editor, createShapeId, TLShapeId } from "@tldraw/tldraw";
import polygonClipping from "polygon-clipping";
import { cloudOutline } from "../state/new-geo-shape-helpers";

// Types from polygon-clipping
type Position = [number, number];
type Ring = Position[];
type Polygon = Ring[];
type MultiPolygon = Polygon[];

/**
 * Helper function to get the color from a shape
 * Tries different color properties (fill, color, stroke) and returns the first valid one
 */
export function getShapeColor(shape: TLShape): string | null {
  const props = shape.props as any;

  // Try colorMix first (for custom shapes with multiple colors)
  if (props.colorMix) {
    // Try numeric keys first (standard format: "0", "1", etc.)
    if (props.colorMix["0"] && props.colorMix["0"].color) {
      return props.colorMix["0"].color;
    }

    // Fallback: check all keys
    const colorMixKeys = Object.keys(props.colorMix);
    if (colorMixKeys.length > 0) {
      const firstColorKey = colorMixKeys[0];
      const colorValue = props.colorMix[firstColorKey];
      if (colorValue && typeof colorValue === "object" && colorValue.color && !colorValue.isHidden) {
        return colorValue.color;
      }
    }
  }

  // Try different color properties in order of preference
  if (props.fill && typeof props.fill === "string") {
    return props.fill;
  }
  if (props.color && typeof props.color === "string") {
    return props.color;
  }
  if (props.stroke && typeof props.stroke === "string") {
    return props.stroke;
  }

  return null;
}

/**
 * Helper function to detect if a shape is an image
 */
function isImageShape(shape: TLShape): boolean {
  return shape.type === "customImage";
}

/**
 * Helper function to get image properties from the first image shape
 */
function getImageProperties(shapes: TLShape[], editor: Editor): any | null {
  const imageShape = shapes.find(isImageShape);
  if (!imageShape) return null;

  const props = imageShape.props as any;
  const bounds = editor.getShapePageBounds(imageShape.id);

  return {
    assetId: props.assetId,
    url: props.url,
    crop: props.crop,
    flipX: props.flipX,
    flipY: props.flipY,
    // Original dimensions and position
    originalW: props.w,
    originalH: props.h,
    originalX: bounds?.x || 0,
    originalY: bounds?.y || 0,
  };
}

// --- Naming helpers for boolean results and groups ---
const operationLabelMap: Record<string, string> = {
  union: "Union",
  subtract: "Subtract",
  substract: "Subtract", // common typo
  intersect: "Intersect",
  exclude: "Exclude",
};

export function getOperationLabel(operation: string): string {
  const label = operationLabelMap[operation?.toLowerCase()];
  if (label) return label;
  if (!operation) return "Union";
  return operation.charAt(0).toUpperCase() + operation.slice(1);
}

function getNextBooleanIndex(label: string, editor: Editor): number {
  const snapshot = editor.getSnapshot();
  const shapes = Object.values(snapshot.document.store) as any[];
  const nums = shapes
    .map((s) => s?.meta?.name as string)
    .filter(Boolean)
    .map((name) => {
      const re = new RegExp(`^${label}\\s*(\\d+)(?:\\s*Group)?$`, "i");
      const m = name.match(re);
      return m ? parseInt(m[1], 10) : null;
    })
    .filter((n): n is number => n !== null);

  if (!nums.length) return 1;
  const sorted = [...nums].sort((a, b) => a - b);
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i] !== i + 1) return i + 1;
  }
  return sorted[sorted.length - 1] + 1;
}

function parseBooleanIndexFromName(name: string | undefined, label: string): number | null {
  if (!name) return null;
  const re = new RegExp(`^${label}\\s*(\\d+)(?:\\s*Group)?$`, "i");
  const m = name.match(re);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Generate star points using the same algorithm as CustomStarUtil
 * x, y are the top-left corner of the bounding box
 */
function getStarPoints(w: number, h: number, x: number, y: number): Position[] {
  const sides = 5;
  const step = (Math.PI * 2) / sides / 2;

  const rightMostIndex = Math.floor(sides / 4) * 2;
  const leftMostIndex = sides * 2 - rightMostIndex;
  const topMostIndex = 0;
  const bottomMostIndex = Math.floor(sides / 2) * 2;

  const maxX = (Math.cos(-Math.PI / 2 + rightMostIndex * step) * w) / 2;
  const minX = (Math.cos(-Math.PI / 2 + leftMostIndex * step) * w) / 2;
  const minY = (Math.sin(-Math.PI / 2 + topMostIndex * step) * h) / 2;
  const maxY = (Math.sin(-Math.PI / 2 + bottomMostIndex * step) * h) / 2;

  const diffX = w - Math.abs(maxX - minX);
  const diffY = h - Math.abs(maxY - minY);
  const offsetX = w / 2 + minX - (w / 2 - maxX);
  const offsetY = h / 2 + minY - (h / 2 - maxY);

  // These calculations match CustomStarUtil.component exactly
  const ratio = 1;
  const cx = (w - offsetX) / 2;
  const cy = (h - offsetY) / 2;
  const ox = (w + diffX) / 2;
  const oy = (h + diffY) / 2;
  const ix = (ox * ratio) / 2;
  const iy = (oy * ratio) / 2;

  const points: Position[] = [];

  for (let i = 0; i < sides * 2; i++) {
    const theta = -Math.PI / 2 + i * step;

    // Match the exact formula from CustomStarUtil
    const px = x + cx + (i % 2 ? ix : ox) * Math.cos(theta);
    const py = y + cy + (i % 2 ? iy : oy) * Math.sin(theta);

    points.push([px, py]);
  }

  return points;
}

/**
 * Generate heart points by sampling the Bézier curves
 * Based on getHeartParts from new-geo-shape-helpers.ts
 */
function getHeartPoints(w: number, h: number, x: number, y: number): Position[] {
  const o = w / 4;
  const k = h / 4;

  // Heart is made of 4 cubic Bézier curves
  const curves = [
    // Bottom left curve
    {
      start: [w / 2, h],
      cp1: [o * 1.5, k * 3],
      cp2: [0, k * 2.5],
      end: [0, k * 1.2],
    },
    // Top left curve
    {
      start: [0, k * 1.2],
      cp1: [0, -k * 0.32],
      cp2: [o * 1.85, -k * 0.32],
      end: [w / 2, k * 0.9],
    },
    // Top right curve
    {
      start: [w / 2, k * 0.9],
      cp1: [w - o * 1.85, -k * 0.32],
      cp2: [w, -k * 0.32],
      end: [w, k * 1.2],
    },
    // Bottom right curve
    {
      start: [w, k * 1.2],
      cp1: [w, k * 2.5],
      cp2: [w - o * 1.5, k * 3],
      end: [w / 2, h],
    },
  ];

  const points: Position[] = [];
  const samplesPerCurve = 16; // 16 samples per curve = 64 total points

  curves.forEach((curve) => {
    for (let i = 0; i < samplesPerCurve; i++) {
      const t = i / samplesPerCurve;
      // Cubic Bézier formula: B(t) = (1-t)³P₀ + 3(1-t)²tP₁ + 3(1-t)t²P₂ + t³P₃
      const t1 = 1 - t;
      const t1_2 = t1 * t1;
      const t1_3 = t1_2 * t1;
      const t_2 = t * t;
      const t_3 = t_2 * t;

      const px =
        x + t1_3 * curve.start[0] + 3 * t1_2 * t * curve.cp1[0] + 3 * t1 * t_2 * curve.cp2[0] + t_3 * curve.end[0];

      const py =
        y + t1_3 * curve.start[1] + 3 * t1_2 * t * curve.cp1[1] + 3 * t1 * t_2 * curve.cp2[1] + t_3 * curve.end[1];

      points.push([px, py]);
    }
  });

  return points;
}

/**
 * Generate cloud points using the actual TLDraw cloud outline function
 * This ensures perfect accuracy with the rendered cloud shape
 */
function getCloudPoints(w: number, h: number, x: number, y: number, shapeId: string): Position[] {
  // Use the exact same function that renders the cloud
  const size = "m" as const; // default size
  const scale = 1; // default scale

  // Get the cloud outline points from TLDraw's helper
  // @ts-ignore - size type mismatch with cloudOutline signature
  const outlinePoints = cloudOutline(w, h, shapeId, size, scale);

  // Convert Vec points to Position format and translate to page coordinates
  const points: Position[] = outlinePoints.map((vec) => [x + vec.x, y + vec.y]);

  return points;
}

/**
 * Parse an SVG path string and extract points
 * Handles both full SVG strings and raw path data
 */
function getSvgPoints(svgPathOrFullSvg: string, w: number, h: number, x: number, y: number): Position[] {
  if (!svgPathOrFullSvg || svgPathOrFullSvg.trim() === "") {
    // Fallback to rectangle
    return [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
    ];
  }

  let pathData = svgPathOrFullSvg;
  let viewBoxWidth = w;
  let viewBoxHeight = h;
  let viewBoxX = 0;
  let viewBoxY = 0;

  // Check if it's a full SVG (contains <svg> tag)
  if (svgPathOrFullSvg.includes("<svg") || svgPathOrFullSvg.includes("<path")) {
    try {
      // Parse the SVG to extract path data
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgPathOrFullSvg, "image/svg+xml");
      const svgElement = svgDoc.querySelector("svg");

      // Collect path data from ALL path elements, plus other shapes
      const paths: string[] = [];

      // Get all <path> elements
      const pathElements = svgDoc.querySelectorAll("path");
      pathElements.forEach((pathEl) => {
        const d = pathEl.getAttribute("d");
        if (d) paths.push(d);
      });

      // Convert <rect> to path
      const rectElements = svgDoc.querySelectorAll("rect");
      rectElements.forEach((rect) => {
        const rx = parseFloat(rect.getAttribute("x") || "0");
        const ry = parseFloat(rect.getAttribute("y") || "0");
        const rw = parseFloat(rect.getAttribute("width") || "0");
        const rh = parseFloat(rect.getAttribute("height") || "0");
        if (rw > 0 && rh > 0) {
          paths.push(`M ${rx} ${ry} L ${rx + rw} ${ry} L ${rx + rw} ${ry + rh} L ${rx} ${ry + rh} Z`);
        }
      });

      // Convert <circle> to approximate path (16 segments)
      const circleElements = svgDoc.querySelectorAll("circle");
      circleElements.forEach((circle) => {
        const cx = parseFloat(circle.getAttribute("cx") || "0");
        const cy = parseFloat(circle.getAttribute("cy") || "0");
        const r = parseFloat(circle.getAttribute("r") || "0");
        if (r > 0) {
          let circlePath = "";
          for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2;
            const px = cx + r * Math.cos(angle);
            const py = cy + r * Math.sin(angle);
            circlePath += i === 0 ? `M ${px} ${py}` : ` L ${px} ${py}`;
          }
          circlePath += " Z";
          paths.push(circlePath);
        }
      });

      // Convert <ellipse> to approximate path
      const ellipseElements = svgDoc.querySelectorAll("ellipse");
      ellipseElements.forEach((ellipse) => {
        const cx = parseFloat(ellipse.getAttribute("cx") || "0");
        const cy = parseFloat(ellipse.getAttribute("cy") || "0");
        const rx = parseFloat(ellipse.getAttribute("rx") || "0");
        const ry = parseFloat(ellipse.getAttribute("ry") || "0");
        if (rx > 0 && ry > 0) {
          let ellipsePath = "";
          for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2;
            const px = cx + rx * Math.cos(angle);
            const py = cy + ry * Math.sin(angle);
            ellipsePath += i === 0 ? `M ${px} ${py}` : ` L ${px} ${py}`;
          }
          ellipsePath += " Z";
          paths.push(ellipsePath);
        }
      });

      // Convert <polygon> and <polyline> to path
      const polygonElements = svgDoc.querySelectorAll("polygon, polyline");
      polygonElements.forEach((poly) => {
        const points = poly.getAttribute("points");
        if (points) {
          const coords = points
            .trim()
            .split(/[\s,]+/)
            .map(parseFloat);
          if (coords.length >= 4) {
            let polyPath = `M ${coords[0]} ${coords[1]}`;
            for (let i = 2; i < coords.length; i += 2) {
              polyPath += ` L ${coords[i]} ${coords[i + 1]}`;
            }
            if (poly.tagName.toLowerCase() === "polygon") {
              polyPath += " Z";
            }
            paths.push(polyPath);
          }
        }
      });

      // Combine all paths
      pathData = paths.join(" ");

      // Extract viewBox for proper scaling
      if (svgElement && svgElement.hasAttribute("viewBox")) {
        const viewBox = svgElement.getAttribute("viewBox") || "";
        const viewBoxParts = viewBox.split(/[\s,]+/);
        if (viewBoxParts.length === 4) {
          viewBoxX = parseFloat(viewBoxParts[0]);
          viewBoxY = parseFloat(viewBoxParts[1]);
          viewBoxWidth = parseFloat(viewBoxParts[2]);
          viewBoxHeight = parseFloat(viewBoxParts[3]);
        }
      } else if (svgElement) {
        // Use width/height attributes if no viewBox
        const widthAttr = svgElement.getAttribute("width");
        const heightAttr = svgElement.getAttribute("height");
        if (widthAttr && heightAttr) {
          viewBoxWidth = parseFloat(widthAttr);
          viewBoxHeight = parseFloat(heightAttr);
        }
      }
    } catch (error) {
      // Fallback to rectangle
      return [
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
      ];
    }
  }

  if (!pathData || pathData.trim() === "") {
    // Fallback to rectangle
    return [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
    ];
  }

  // Calculate scale factors to map from viewBox to actual shape size
  const scaleX = w / viewBoxWidth;
  const scaleY = h / viewBoxHeight;

  const points: Position[] = [];

  // Parse SVG path commands
  const commands = pathData.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g) || [];
  let currentX = 0;
  let currentY = 0;
  let startX = 0;
  let startY = 0;
  let lastControlX = 0; // For smooth curves (S, s commands)
  let lastControlY = 0;

  commands.forEach((cmd) => {
    const type = cmd[0];
    const args = cmd
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .map(parseFloat)
      .filter((n) => !isNaN(n));

    switch (type) {
      case "M": // Move to (absolute)
        currentX = args[0];
        currentY = args[1];
        startX = currentX;
        startY = currentY;
        points.push([x + (currentX - viewBoxX) * scaleX, y + (currentY - viewBoxY) * scaleY]);
        break;

      case "m": // Move to (relative)
        currentX += args[0];
        currentY += args[1];
        startX = currentX;
        startY = currentY;
        points.push([x + (currentX - viewBoxX) * scaleX, y + (currentY - viewBoxY) * scaleY]);
        break;

      case "L": // Line to (absolute)
        for (let i = 0; i < args.length; i += 2) {
          currentX = args[i];
          currentY = args[i + 1];
          points.push([x + (currentX - viewBoxX) * scaleX, y + (currentY - viewBoxY) * scaleY]);
        }
        break;

      case "l": // Line to (relative)
        for (let i = 0; i < args.length; i += 2) {
          currentX += args[i];
          currentY += args[i + 1];
          points.push([x + (currentX - viewBoxX) * scaleX, y + (currentY - viewBoxY) * scaleY]);
        }
        break;

      case "H": // Horizontal line (absolute)
        currentX = args[0];
        points.push([x + (currentX - viewBoxX) * scaleX, y + (currentY - viewBoxY) * scaleY]);
        break;

      case "h": // Horizontal line (relative)
        currentX += args[0];
        points.push([x + (currentX - viewBoxX) * scaleX, y + (currentY - viewBoxY) * scaleY]);
        break;

      case "V": // Vertical line (absolute)
        currentY = args[0];
        points.push([x + (currentX - viewBoxX) * scaleX, y + (currentY - viewBoxY) * scaleY]);
        break;

      case "v": // Vertical line (relative)
        currentY += args[0];
        points.push([x + (currentX - viewBoxX) * scaleX, y + (currentY - viewBoxY) * scaleY]);
        break;

      case "C": // Cubic Bézier (absolute)
        for (let i = 0; i < args.length; i += 6) {
          const cp1x = args[i];
          const cp1y = args[i + 1];
          const cp2x = args[i + 2];
          const cp2y = args[i + 3];
          const endX = args[i + 4];
          const endY = args[i + 5];

          // Sample 8 points along the curve
          for (let t = 0.125; t <= 1; t += 0.125) {
            const t1 = 1 - t;
            const px = t1 * t1 * t1 * currentX + 3 * t1 * t1 * t * cp1x + 3 * t1 * t * t * cp2x + t * t * t * endX;
            const py = t1 * t1 * t1 * currentY + 3 * t1 * t1 * t * cp1y + 3 * t1 * t * t * cp2y + t * t * t * endY;
            points.push([x + (px - viewBoxX) * scaleX, y + (py - viewBoxY) * scaleY]);
          }

          lastControlX = cp2x;
          lastControlY = cp2y;
          currentX = endX;
          currentY = endY;
        }
        break;

      case "c": // Cubic Bézier (relative)
        for (let i = 0; i < args.length; i += 6) {
          const startXForCurve = currentX;
          const startYForCurve = currentY;
          const cp1x = currentX + args[i];
          const cp1y = currentY + args[i + 1];
          const cp2x = currentX + args[i + 2];
          const cp2y = currentY + args[i + 3];
          const endX = currentX + args[i + 4];
          const endY = currentY + args[i + 5];

          for (let t = 0.125; t <= 1; t += 0.125) {
            const t1 = 1 - t;
            const px =
              t1 * t1 * t1 * startXForCurve + 3 * t1 * t1 * t * cp1x + 3 * t1 * t * t * cp2x + t * t * t * endX;
            const py =
              t1 * t1 * t1 * startYForCurve + 3 * t1 * t1 * t * cp1y + 3 * t1 * t * t * cp2y + t * t * t * endY;
            points.push([x + (px - viewBoxX) * scaleX, y + (py - viewBoxY) * scaleY]);
          }

          lastControlX = cp2x;
          lastControlY = cp2y;
          currentX = endX;
          currentY = endY;
        }
        break;

      case "S": // Smooth cubic Bézier (absolute)
        for (let i = 0; i < args.length; i += 4) {
          // Reflect last control point
          const cp1x = 2 * currentX - lastControlX;
          const cp1y = 2 * currentY - lastControlY;
          const cp2x = args[i];
          const cp2y = args[i + 1];
          const endX = args[i + 2];
          const endY = args[i + 3];

          for (let t = 0.125; t <= 1; t += 0.125) {
            const t1 = 1 - t;
            const px = t1 * t1 * t1 * currentX + 3 * t1 * t1 * t * cp1x + 3 * t1 * t * t * cp2x + t * t * t * endX;
            const py = t1 * t1 * t1 * currentY + 3 * t1 * t1 * t * cp1y + 3 * t1 * t * t * cp2y + t * t * t * endY;
            points.push([x + (px - viewBoxX) * scaleX, y + (py - viewBoxY) * scaleY]);
          }

          lastControlX = cp2x;
          lastControlY = cp2y;
          currentX = endX;
          currentY = endY;
        }
        break;

      case "s": // Smooth cubic Bézier (relative)
        for (let i = 0; i < args.length; i += 4) {
          const startXForCurve = currentX;
          const startYForCurve = currentY;
          const cp1x = 2 * currentX - lastControlX;
          const cp1y = 2 * currentY - lastControlY;
          const cp2x = currentX + args[i];
          const cp2y = currentY + args[i + 1];
          const endX = currentX + args[i + 2];
          const endY = currentY + args[i + 3];

          for (let t = 0.125; t <= 1; t += 0.125) {
            const t1 = 1 - t;
            const px =
              t1 * t1 * t1 * startXForCurve + 3 * t1 * t1 * t * cp1x + 3 * t1 * t * t * cp2x + t * t * t * endX;
            const py =
              t1 * t1 * t1 * startYForCurve + 3 * t1 * t1 * t * cp1y + 3 * t1 * t * t * cp2y + t * t * t * endY;
            points.push([x + (px - viewBoxX) * scaleX, y + (py - viewBoxY) * scaleY]);
          }

          lastControlX = cp2x;
          lastControlY = cp2y;
          currentX = endX;
          currentY = endY;
        }
        break;

      case "Q": // Quadratic Bézier (absolute)
        for (let i = 0; i < args.length; i += 4) {
          const cpx = args[i];
          const cpy = args[i + 1];
          const endX = args[i + 2];
          const endY = args[i + 3];

          for (let t = 0.167; t <= 1; t += 0.167) {
            const t1 = 1 - t;
            const px = t1 * t1 * currentX + 2 * t1 * t * cpx + t * t * endX;
            const py = t1 * t1 * currentY + 2 * t1 * t * cpy + t * t * endY;
            points.push([x + (px - viewBoxX) * scaleX, y + (py - viewBoxY) * scaleY]);
          }

          lastControlX = cpx;
          lastControlY = cpy;
          currentX = endX;
          currentY = endY;
        }
        break;

      case "q": // Quadratic Bézier (relative)
        for (let i = 0; i < args.length; i += 4) {
          const startXForCurve = currentX;
          const startYForCurve = currentY;
          const cpx = currentX + args[i];
          const cpy = currentY + args[i + 1];
          const endX = currentX + args[i + 2];
          const endY = currentY + args[i + 3];

          for (let t = 0.167; t <= 1; t += 0.167) {
            const t1 = 1 - t;
            const px = t1 * t1 * startXForCurve + 2 * t1 * t * cpx + t * t * endX;
            const py = t1 * t1 * startYForCurve + 2 * t1 * t * cpy + t * t * endY;
            points.push([x + (px - viewBoxX) * scaleX, y + (py - viewBoxY) * scaleY]);
          }

          lastControlX = cpx;
          lastControlY = cpy;
          currentX = endX;
          currentY = endY;
        }
        break;

      case "A": // Arc (absolute) - approximate with line segments
      case "a": // Arc (relative)
        // For simplicity, approximate arc with endpoint
        if (args.length >= 7) {
          const isRelative = type === "a";
          const endX = isRelative ? currentX + args[5] : args[5];
          const endY = isRelative ? currentY + args[6] : args[6];

          // Simple approximation: draw 4 intermediate points
          for (let i = 1; i <= 4; i++) {
            const t = i / 4;
            const px = currentX + (endX - currentX) * t;
            const py = currentY + (endY - currentY) * t;
            points.push([x + (px - viewBoxX) * scaleX, y + (py - viewBoxY) * scaleY]);
          }

          currentX = endX;
          currentY = endY;
        }
        break;

      case "Z": // Close path
      case "z":
        if (points.length > 0 && (currentX !== startX || currentY !== startY)) {
          points.push([x + (startX - viewBoxX) * scaleX, y + (startY - viewBoxY) * scaleY]);
        }
        currentX = startX;
        currentY = startY;
        break;

      // For unsupported commands, just add the endpoint
      default:
        if (args.length >= 2) {
          const isRelative = type === type.toLowerCase();
          if (isRelative) {
            currentX += args[args.length - 2];
            currentY += args[args.length - 1];
          } else {
            currentX = args[args.length - 2];
            currentY = args[args.length - 1];
          }
          points.push([x + (currentX - viewBoxX) * scaleX, y + (currentY - viewBoxY) * scaleY]);
        }
        break;
    }
  });

  return points;
}

/**
 * Generate triangle points
 * x, y are the top-left corner of the bounding box
 */
function getTrianglePoints(w: number, h: number, x: number, y: number): Position[] {
  // Triangle with top vertex at center-top, base at bottom
  return [
    [x + w / 2, y], // Top center
    [x + w, y + h], // Bottom right
    [x, y + h], // Bottom left
  ];
}

/**
 * Converts a TLDraw shape to a polygon format compatible with polygon-clipping
 */
export function shapeToPolygon(shape: TLShape, editor: Editor): Polygon {
  try {
    const geometry = editor.getShapeGeometry(shape.id);
    const bounds = editor.getShapePageBounds(shape.id);

    if (!bounds) {
      return [];
    }

    // Get shape dimensions from props (these are always in local coordinates)
    // Use props.w and props.h to ensure we're using the actual shape size, not the transformed bounds
    const props = shape.props as any;
    const w = props.w !== undefined ? props.w : bounds.width;
    const h = props.h !== undefined ? props.h : bounds.height;

    // Use page bounds for positioning (these are in page coordinates, accounting for group transforms)
    const x = bounds.x; // Top-left corner X in page coordinates
    const y = bounds.y; // Top-left corner Y in page coordinates

    // Handle star shape with precise geometry
    if (shape.type === "customStar") {
      const points = getStarPoints(w, h, x, y);
      points.push(points[0]); // Close the ring
      return [points];
    }

    // Handle triangle shape with precise geometry
    if (shape.type === "customTriangle") {
      const points = getTrianglePoints(w, h, x, y);
      points.push(points[0]); // Close the ring
      return [points];
    }

    // Handle heart shape with precise geometry
    if (shape.type === "customHeart") {
      const points = getHeartPoints(w, h, x, y);
      points.push(points[0]); // Close the ring
      return [points];
    }

    // Handle cloud shape with precise geometry
    if (shape.type === "customCloud") {
      const points = getCloudPoints(w, h, x, y, shape.id);
      points.push(points[0]); // Close the ring
      return [points];
    }

    // Handle customSvg shapes (icons from the icons panel)
    if (shape.type === "customSvg") {
      const svgPath = (shape.props as any).svgPath || "";
      const points = getSvgPoints(svgPath, w, h, x, y);
      if (points.length > 0) {
        points.push(points[0]); // Close the ring
      }
      return [points];
    }

    // For shapes with curves (ellipse, oval), we need more points
    // to approximate the curves properly
    const curvedShapes = ["customEllipse", "customOval"];
    const isCurvedShape = curvedShapes.includes(shape.type);

    if (isCurvedShape) {
      // For ellipse/oval, use parametric equation for perfect circles/ellipses
      const numPoints = 64; // More points for smoother curves
      const ring: Ring = [];

      for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * Math.PI * 2;

        // Ellipse/Oval: parametric equation
        const rx = w / 2;
        const ry = h / 2;
        const centerX = x + rx;
        const centerY = y + ry;
        const px = centerX + rx * Math.cos(angle);
        const py = centerY + ry * Math.sin(angle);

        ring.push([px, py] as Position);
      }

      // Close the ring
      if (ring.length > 0) {
        ring.push([ring[0][0], ring[0][1]]);
      }

      return [ring];
    }

    // For non-curved shapes, use vertices as before
    const vertices = geometry.vertices;

    if (vertices && vertices.length > 0) {
      // Convert vertices to page coordinates
      const ring: Ring = vertices.map((vertex) => {
        return [x + vertex.x, y + vertex.y] as Position;
      });

      // Close the ring if not already closed
      const firstPoint = ring[0];
      const lastPoint = ring[ring.length - 1];
      if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        ring.push([firstPoint[0], firstPoint[1]]);
      }

      return [ring];
    }

    // Fallback: create a rectangle from bounds
    const ring: Ring = [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
      [x, y], // Close the ring
    ];

    return [ring];
  } catch (error) {
    return [];
  }
}

/**
 * Converts a polygon-clipping result (MultiPolygon) to SVG path data
 */
export function polygonToSVGPath(polygon: MultiPolygon): string {
  if (!polygon || polygon.length === 0) {
    return "";
  }

  let pathData = "";

  polygon.forEach((poly) => {
    poly.forEach((ring, ringIndex) => {
      ring.forEach((point, pointIndex) => {
        if (pointIndex === 0) {
          pathData += `M ${point[0]} ${point[1]} `;
        } else {
          pathData += `L ${point[0]} ${point[1]} `;
        }
      });
      pathData += "Z ";
    });
  });

  return pathData.trim();
}

/**
 * Calculates the clip-path relative to a specific shape's coordinates
 * This is needed because CSS clip-path uses coordinates relative to the element
 */
export function calculateRelativeClipPath(absolutePath: string, shape: TLShape, editor: Editor): string {
  if (!absolutePath) return "";

  const bounds = editor.getShapePageBounds(shape.id);
  if (!bounds) return "";

  // Parse the SVG path and translate coordinates to be relative to the shape
  // The path is in page coordinates, we need to convert to shape-local coordinates
  const pathRegex = /([ML])\s*([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)/g;

  const relativePath = absolutePath.replace(pathRegex, (match, command, x, y) => {
    const absX = parseFloat(x);
    const absY = parseFloat(y);

    // Convert from page coordinates to shape-local coordinates
    const relX = absX - bounds.x;
    const relY = absY - bounds.y;

    return `${command} ${relX} ${relY}`;
  });

  return relativePath;
}

/**
 * Calculates the bounding box of a MultiPolygon
 */
export function getPolygonBounds(polygon: MultiPolygon) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  polygon.forEach((poly) => {
    poly.forEach((ring) => {
      ring.forEach((point) => {
        minX = Math.min(minX, point[0]);
        minY = Math.min(minY, point[1]);
        maxX = Math.max(maxX, point[0]);
        maxY = Math.max(maxY, point[1]);
      });
    });
  });

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * Performs a boolean operation on multiple shapes
 */
export function performBooleanOperation(
  shapes: TLShape[],
  operation: "union" | "subtract" | "intersect" | "exclude",
  editor: Editor
): { svgPath: string; bounds: ReturnType<typeof getPolygonBounds>; isEmpty: boolean } | null {
  try {
    if (shapes.length < 2) {
      return null;
    }

    // Convert all shapes to polygons
    const polygons = shapes.map((shape) => shapeToPolygon(shape, editor)).filter((p) => p.length > 0);

    if (polygons.length < 2) {
      return null;
    }

    let result: MultiPolygon;

    switch (operation) {
      case "union":
        // For union, we combine all polygons at once
        result = polygonClipping.union(polygons[0], ...polygons.slice(1));
        break;

      case "subtract":
        // For subtract, we subtract all subsequent polygons from the first
        result = polygonClipping.difference(polygons[0], ...polygons.slice(1));
        break;

      case "intersect":
        // For intersect, we find the intersection of all polygons
        result = polygons.slice(1).reduce((acc, poly) => {
          return polygonClipping.intersection(acc, poly);
        }, polygons[0] as unknown as MultiPolygon);
        break;

      case "exclude":
        // For exclude (XOR), we need to do it pairwise
        result = polygons.slice(1).reduce((acc, poly) => {
          return polygonClipping.xor(acc, poly);
        }, polygons[0] as unknown as MultiPolygon);
        break;

      default:
        return null;
    }

    // Allow empty results (Figma behavior - shapes become "hidden" with no geometry)
    if (!result) {
      return null;
    }

    // Check if result is empty
    const isEmpty = result.length === 0;

    const svgPath = polygonToSVGPath(result);
    const bounds = isEmpty ? { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 } : getPolygonBounds(result);

    return { svgPath, bounds, isEmpty };
  } catch (error) {
    return null;
  }
}

/**
 * Creates a mask group with real-time clip-path + a mask shape for visualization
 * The original shapes remain in a group with clip-path (editable)
 * A separate mask shape represents the result (visible when not editing)
 */
export function createMaskShape(
  originalShapes: TLShape[],
  resultSvgPath: string,
  bounds: ReturnType<typeof getPolygonBounds>,
  operation: string,
  editor: Editor
): void {
  try {
    const firstShapeColor = getShapeColor(originalShapes[0]);

    // Check if any shape is an image
    const hasImageShape = originalShapes.some(isImageShape);
    const imageProps = hasImageShape ? getImageProperties(originalShapes, editor) : null;

    // Naming for result and group
    const label = getOperationLabel(operation);
    const booleanIndex = getNextBooleanIndex(label, editor);
    const resultName = `${label} ${booleanIndex}`;
    const groupName = resultName; // group named same as result shape

    // Wrap entire mask creation in batch() to make it atomic for undo/redo
    // This prevents Ctrl+Z from leaving the group in an inconsistent state
    let groupId: string | undefined;
    let group: TLShape | undefined;

    editor.batch(() => {
      // 1. Group the original shapes FIRST - without modifying them
      const allShapeIds = originalShapes.map((s) => s.id);
      editor.groupShapes(allShapeIds);

      // Get the created group
      const firstShape = editor.getShape(allShapeIds[0]);
      groupId = firstShape?.parentId;

      if (!groupId || groupId === "page:page") {
        return;
      }

      group = editor.getShape(groupId as TLShapeId);
      if (group && group.type === "group") {
        // Update group metadata
        editor.updateShape({
          ...group,
          meta: {
            ...group.meta,
            isMaskGroup: true,
            maskOperation: operation,
            name: groupName,
            booleanLabel: label,
            booleanIndex,
          },
        });
      }

      // 2. NOW apply clip-path, color, hide and lock to shapes inside the group
      originalShapes.forEach((shape) => {
        const updatedShape = editor.getShape(shape.id);
        if (!updatedShape) return;

        const relativeClipPath = calculateRelativeClipPath(resultSvgPath, updatedShape, editor);
        const updatedProps = { ...updatedShape.props } as any;

        // Apply color from first shape
        if (firstShapeColor) {
          if (updatedProps.colorMix && typeof updatedProps.colorMix === "object") {
            updatedProps.colorMix = { ...updatedProps.colorMix };
            Object.keys(updatedProps.colorMix).forEach((key) => {
              if (updatedProps.colorMix[key] && typeof updatedProps.colorMix[key] === "object") {
                updatedProps.colorMix[key] = {
                  ...updatedProps.colorMix[key],
                  color: firstShapeColor,
                };
              }
            });
          }
        }

        const originalOpacity = updatedShape.opacity ?? 1;

        editor.updateShape({
          ...updatedShape,
          opacity: 0, // Hide by default - only shown in edit mode
          isLocked: true, // Lock to prevent selection when hidden
          props: updatedProps,
          meta: {
            ...updatedShape.meta,
            maskClipPath: relativeClipPath,
            isInMaskGroup: true,
            maskOperation: operation,
            originalOpacity, // keep original opacity to restore in edit mode (shapes-only)
          },
        });
      });

      // Check if group was created successfully before proceeding
      if (!groupId || !group) {
        return;
      }

      // 3. Create the mask shape (visible result representation)
      const maskShapeId = createShapeId();

      // Create mask shape based on type
      if (hasImageShape && imageProps) {
        // For image masks, create a single customImage with clip-path

        const imageOffsetX = imageProps.originalX - bounds.minX;
        const imageOffsetY = imageProps.originalY - bounds.minY;

        // For union/exclude: scale image to fill entire result bounds (use as texture)
        // For intersect/subtract: keep original image dimensions (just clip)
        const shouldScaleImage = operation === "union" || operation === "exclude";

        // Calculate clip-path based on operation type
        let imageClipPath: string;

        if (shouldScaleImage) {
          // For union/exclude: clip-path relative to result bounds (image will scale to fill)
          const pathRegex = /([ML])\s*([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)/g;
          imageClipPath = resultSvgPath.replace(pathRegex, (match, command, x, y) => {
            const absX = parseFloat(x);
            const absY = parseFloat(y);
            const relX = absX - bounds.minX; // Relative to result bounds
            const relY = absY - bounds.minY;
            return `${command} ${relX} ${relY}`;
          });
        } else {
          // For intersect/subtract: clip-path relative to original image position
          const pathRegex = /([ML])\s*([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)/g;
          imageClipPath = resultSvgPath.replace(pathRegex, (match, command, x, y) => {
            const absX = parseFloat(x);
            const absY = parseFloat(y);
            const relX = absX - imageProps.originalX;
            const relY = absY - imageProps.originalY;
            return `${command} ${relX} ${relY}`;
          });
        }

        editor.createShape({
          id: maskShapeId,
          type: "customImage",
          x: bounds.minX,
          y: bounds.minY,
          opacity: 1,
          props: {
            w: bounds.width,
            h: bounds.height,
            transparency: 1,
            rounded: 0,
            assetId: imageProps.assetId,
            url: imageProps.url,
            crop: imageProps.crop,
            flipX: imageProps.flipX || false,
            flipY: imageProps.flipY || false,
            playing: false,
            colorMix: {},
            outlineColorMix: {},
            outShadowArray: {},
            inShadowArray: {},
            isHiddenLayerBlur: true,
            isHiddenBorder: true,
            filterBlur: 0,
            borderWidth: 0,
            strokeWidth: 0,
            dash: "solid",
            outlineColor: "transparent",
            fill: "transparent",
          } as any,
          meta: {
            isMaskShape: true,
            maskGroupId: groupId,
            maskOperation: operation,
            maskClipPath: imageClipPath,
            // Only store original dimensions for intersect/subtract (to maintain original size)
            // For union/exclude, omit these so image scales to fill the shape
            ...(shouldScaleImage
              ? {}
              : {
                  originalImageWidth: imageProps.originalW,
                  originalImageHeight: imageProps.originalH,
                  imageOffsetX: imageOffsetX,
                  imageOffsetY: imageOffsetY,
                }),
            isImageMask: true,
            name: resultName,
            booleanLabel: label,
            booleanIndex,
          },
        });
      } else {
        // Create as customRect with color (existing behavior for non-image masks)
        const maskShapeProps = {
          w: bounds.width,
          h: bounds.height,
          transparency: 1,
          rounded: 0,
          colorMix: {
            "0": { color: firstShapeColor || "#e3e3e3", isHidden: false },
          },
          outlineColorMix: {},
          outShadowArray: {},
          inShadowArray: {},
          isHiddenLayerBlur: true,
          isHiddenBorder: true,
          filterBlur: 0,
          borderWidth: 0,
          strokeWidth: 0,
          dash: "solid",
          outlineColor: "transparent",
        };

        editor.createShape({
          id: maskShapeId,
          type: "customRect",
          x: bounds.minX,
          y: bounds.minY,
          opacity: 1,
          props: maskShapeProps as any,
          meta: {
            isMaskShape: true,
            maskGroupId: groupId,
            maskOperation: operation,
            maskClipPath: resultSvgPath,
            name: resultName,
            booleanLabel: label,
            booleanIndex,
          },
        });
      }

      // Convert to relative clip-path (only for customRect, customImage already has it)
      if (!hasImageShape) {
        const createdMaskShape = editor.getShape(maskShapeId);
        if (createdMaskShape) {
          const relativeClipPath = calculateRelativeClipPath(resultSvgPath, createdMaskShape, editor);
          editor.updateShape({
            ...createdMaskShape,
            meta: {
              ...createdMaskShape.meta,
              maskClipPath: relativeClipPath,
            },
          });
        }
      }

      // Update group to reference the mask shape
      const updatedGroup = editor.getShape(groupId as TLShapeId);
      if (updatedGroup) {
        editor.updateShape({
          ...updatedGroup,
          meta: {
            ...updatedGroup.meta,
            maskShapeId: maskShapeId, // Link back to mask shape
          },
        });
      }

      // 4. Select the mask shape (the result)
      editor.select(maskShapeId as any);
    }); // End of editor.batch() - all operations are atomic
  } catch (error) {
    // Error creating mask - ensure batch rollback
  }
}

/**
 * Ungroups a mask group: deletes the mask result shape and the group, reparents the
 * original shapes to the page and clears their mask-related meta.
 */
export function ungroupMaskGroup(maskShapeId: TLShapeId, editor: Editor): void {
  try {
    const maskShape = editor.getShape(maskShapeId);
    if (!maskShape?.meta?.isMaskShape) return;

    const groupId = maskShape.meta.maskGroupId as TLShapeId;
    if (!groupId) return;

    const group = editor.getShape(groupId);
    if (!group || group.type !== "group") return;

    const pageId = editor.getCurrentPageId();
    const childIds = editor.getSortedChildIdsForParent(groupId);
    const children = childIds
      .map((id) => editor.getShape(id))
      .filter((s): s is TLShape => !!s && !!s.meta?.isInMaskGroup);

    editor.run(() => {
      editor.deleteShapes([maskShapeId]);

      const allShapes = editor.getCurrentPageShapes();
      const previewShapes = allShapes.filter((s) => s.meta?.isPreviewShape && s.meta?.previewForGroup === groupId);
      if (previewShapes.length > 0) {
        previewShapes.forEach((s) => editor.updateShape({ ...s, isLocked: false }));
        editor.deleteShapes(previewShapes.map((s) => s.id));
      }

      const idsToReparent = children.map((c) => c.id);
      if (idsToReparent.length > 0) {
        editor.reparentShapes(idsToReparent, pageId);
        // Restore each child to normal: visible (original opacity), unlocked, no clip.
        // tldraw merges meta (does not replace), so we must explicitly set mask keys to null
        // so they are overwritten and renderers stop applying clipPath / mask state.
        children.forEach((child) => {
          const { originalOpacity } = (child.meta || {}) as Record<string, unknown>;
          const opacity = typeof originalOpacity === "number" ? originalOpacity : child.opacity ?? 1;
          editor.updateShape({
            id: child.id,
            type: child.type,
            opacity,
            isLocked: false,
            meta: {
              ...(child.meta || {}),
              isInMaskGroup: null,
              maskClipPath: null,
              originalOpacity: null,
              maskOperation: null,
            },
          });
        });
      }

      editor.deleteShapes([groupId]);
      if (idsToReparent.length > 0) {
        editor.select(...idsToReparent);
      }
    });
  } catch {
    // no-op
  }
}

/**
 * Applies a new boolean operation to an existing mask group (without entering edit mode).
 * Updates group meta, children clip-paths, and mask shape geometry + clip-path using the same logic as exitMaskEditMode.
 * @returns true if the operation was applied, false if result was empty or error
 */
export function applyMaskGroupOperation(
  groupId: TLShapeId,
  maskShapeId: TLShapeId,
  operation: "union" | "subtract" | "intersect" | "exclude",
  editor: Editor
): boolean {
  try {
    const group = editor.getShape(groupId);
    const maskShape = editor.getShape(maskShapeId);
    if (!group || group.type !== "group" || !maskShape?.meta?.isMaskShape) return false;

    const childIds = editor.getSortedChildIdsForParent(groupId);
    const children = childIds
      .map((id) => editor.getShape(id))
      .filter((s): s is TLShape => !!s && !!s.meta?.isInMaskGroup);

    if (children.length < 2) return false;

    const result = performBooleanOperation(children, operation, editor);
    if (!result || result.isEmpty) return false;

    const firstShapeColor = getShapeColor(children[0]);
    const hasImageShape = children.some(isImageShape);
    const imageProps = hasImageShape ? getImageProperties(children, editor) : null;
    const pathRegex = /([ML])\s*([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)/g;

    // 1. Update group meta
    editor.updateShape({
      ...group,
      meta: { ...group.meta, maskOperation: operation },
    });

    // 2. Update each child's maskClipPath (relative to each child's position)
    children.forEach((child) => {
      const clipPath = calculateRelativeClipPath(result.svgPath, child, editor);
      editor.updateShape({
        ...child,
        meta: { ...child.meta, maskClipPath: clipPath },
      });
    });

    // 3. Update mask shape: geometry + clip-path (same logic as exitMaskEditMode)
    const isImageMask = maskShape.type === "customImage" && maskShape.meta?.isImageMask;

    if (isImageMask && imageProps) {
      const shouldScaleImage = operation === "union" || operation === "exclude";
      const imageOffsetX = imageProps.originalX - result.bounds.minX;
      const imageOffsetY = imageProps.originalY - result.bounds.minY;

      let imageClipPath: string;
      if (shouldScaleImage) {
        imageClipPath = result.svgPath.replace(pathRegex, (match, command, x, y) => {
          const absX = parseFloat(x);
          const absY = parseFloat(y);
          return `${command} ${absX - result.bounds.minX} ${absY - result.bounds.minY}`;
        });
      } else {
        imageClipPath = result.svgPath.replace(pathRegex, (match, command, x, y) => {
          const absX = parseFloat(x);
          const absY = parseFloat(y);
          return `${command} ${absX - imageProps.originalX} ${absY - imageProps.originalY}`;
        });
      }

      editor.updateShape({
        ...maskShape,
        x: result.bounds.minX,
        y: result.bounds.minY,
        props: {
          ...maskShape.props,
          w: result.bounds.width,
          h: result.bounds.height,
        },
        meta: {
          ...maskShape.meta,
          maskOperation: operation,
          maskClipPath: imageClipPath,
          ...(shouldScaleImage
            ? {}
            : {
                originalImageWidth: imageProps.originalW,
                originalImageHeight: imageProps.originalH,
                imageOffsetX,
                imageOffsetY,
              }),
        },
      });
    } else {
      // customRect or non-image: clip-path relative to new shape bounds (result.bounds)
      const relativeClipPath = result.svgPath.replace(pathRegex, (match, command, x, y) => {
        const absX = parseFloat(x);
        const absY = parseFloat(y);
        return `${command} ${absX - result.bounds.minX} ${absY - result.bounds.minY}`;
      });

      editor.updateShape({
        ...maskShape,
        x: result.bounds.minX,
        y: result.bounds.minY,
        props: {
          ...maskShape.props,
          w: result.bounds.width,
          h: result.bounds.height,
          colorMix: {
            "0": { color: firstShapeColor || "#e3e3e3", isHidden: false },
          },
        },
        meta: {
          ...maskShape.meta,
          maskOperation: operation,
          maskClipPath: relativeClipPath,
        },
      });
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Enters edit mode for a mask shape
 * Hides the mask result shape and shows the mask group for editing
 * @param options.selectGroup - When true (e.g. from Layers panel), select the group so edit mode stays active. When false (e.g. from canvas double-click), leave selection empty; the hook sets lastSelectionRef so we don't exit.
 */
export function enterMaskEditMode(maskShapeId: TLShapeId, editor: Editor, options?: { selectGroup?: boolean }): void {
  try {
    const maskShape = editor.getShape(maskShapeId);
    if (!maskShape || !maskShape.meta?.isMaskShape) {
      return;
    }

    const groupId = maskShape.meta.maskGroupId as TLShapeId;
    if (!groupId) {
      return;
    }

    // Get the mask operation before deleting the mask shape
    const maskOperation = maskShape.meta.maskOperation as string;
    const shouldCreatePreview = maskOperation === "union" || maskOperation === "exclude";
    const previewLabel = getOperationLabel(maskOperation);

    // DELETE the mask shape immediately (will be recreated on exit)
    editor.deleteShapes([maskShapeId]);
    // Show all shapes in the mask group
    const group = editor.getShape(groupId);
    if (group && group.type === "group") {
      const childIds = editor.getSortedChildIdsForParent(groupId);
      const children = childIds.map((id) => editor.getShape(id)).filter(Boolean) as TLShape[];

      // Check if we have an image shape
      const hasImage = children.some(isImageShape);

      // STEP 1: Create preview shape FIRST (so it stays below in z-order)
      if (shouldCreatePreview && hasImage) {
        // Calculate current boolean operation
        const result = performBooleanOperation(children, maskOperation as any, editor);

        if (result && !result.isEmpty) {
          // Get image properties
          const imageProps = getImageProperties(children, editor);

          if (imageProps) {
            const previewId = createShapeId();

            // Calculate clip-path relative to result bounds (for projection)
            const pathRegex = /([ML])\s*([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)/g;
            const previewClipPath = result.svgPath.replace(pathRegex, (match, command, x, y) => {
              const absX = parseFloat(x);
              const absY = parseFloat(y);
              const relX = absX - result.bounds.minX;
              const relY = absY - result.bounds.minY;
              return `${command} ${relX} ${relY}`;
            });

            // Create preview shape with image fill projected onto boolean geometry
            editor.createShape({
              id: previewId,
              type: "customImage",
              x: result.bounds.minX,
              y: result.bounds.minY,
              opacity: 1, // Preview fully opaque
              isLocked: true, // Prevent selection (important!)
              props: {
                w: result.bounds.width,
                h: result.bounds.height,
                transparency: 1,
                rounded: 0,
                assetId: imageProps.assetId,
                url: imageProps.url,
                crop: imageProps.crop,
                flipX: imageProps.flipX || false,
                flipY: imageProps.flipY || false,
                playing: false,
                colorMix: {},
                outlineColorMix: {},
                outShadowArray: {},
                inShadowArray: {},
                isHiddenLayerBlur: true,
                isHiddenBorder: true,
                filterBlur: 0,
                borderWidth: 0,
                strokeWidth: 0,
                dash: "solid",
                outlineColor: "transparent",
                fill: "transparent",
              } as any,
              meta: {
                isPreviewShape: true, // Mark as preview
                isMaskShape: true, // ✅ Required for CustomImageUtil to recognize and apply clip-path
                previewForGroup: groupId,
                maskClipPath: previewClipPath,
                isImageMask: true,
                booleanLabel: previewLabel,
                name: `${previewLabel} Preview`,
                // No originalImageWidth/Height - let image scale to fill
              },
            });
          }
        }
      }

      // STEP 2: Show/unlock operands AFTER preview (so they're above in z-order)
      children.forEach((child) => {
        if (child.meta?.isInMaskGroup) {
          let childOpacity: number;

          if (hasImage) {
            // For union/exclude with preview:
            // - Hide the original image (preview shows it)
            // - Make shapes barely visible (just outlines)
            if (shouldCreatePreview) {
              childOpacity = isImageShape(child) ? 0 : 0.05;
            } else {
              // For intersect/subtract with image but no preview
              childOpacity = isImageShape(child) ? 1 : 0.05;
            }
          } else {
            // No images: show shapes with their original opacity (stored on creation)
            const stored = child.meta?.originalOpacity;
            childOpacity = typeof stored === "number" ? stored : child.opacity !== undefined ? child.opacity : 1;
          }

          editor.updateShape({
            ...child,
            opacity: childOpacity,
            isLocked: false, // Unlock for editing
          });
        }
      });

      // Update group metadata to mark we're in edit mode
      editor.updateShape({
        id: groupId,
        type: "group",
        meta: {
          ...group.meta,
          inEditMode: true,
        },
      });
    }

    if (options?.selectGroup) {
      editor.select(groupId as any);
    } else {
      editor.setSelectedShapes([]);
    }
  } catch (error) {
    // Error entering mask edit mode
  }
}

/**
 * Exits edit mode for a mask shape
 */
export function exitMaskEditMode(maskShapeId: TLShapeId, editor: Editor): void {
  try {
    // Note: maskShapeId might not exist anymore (was deleted on enter)
    // We need to get the group from the stored metadata

    // Find the group that has inEditMode = true
    const allShapes = editor.getCurrentPageShapes();
    let maskGroup = allShapes.find((s) => s.type === "group" && s.meta?.isMaskGroup && s.meta?.inEditMode === true);

    // Fallback: If no group is in edit mode, try to find by maskShapeId reference
    if (!maskGroup) {
      // Try to find a mask group that references this maskShapeId
      maskGroup = allShapes.find(
        (s) => s.type === "group" && s.meta?.isMaskGroup && s.meta?.maskShapeId === maskShapeId
      );

      // If still not found, look for any mask group with visible children (opacity > 0)
      if (!maskGroup) {
        const possibleGroups = allShapes.filter((s) => s.type === "group" && s.meta?.isMaskGroup);

        for (const group of possibleGroups) {
          const children = editor
            .getSortedChildIdsForParent(group.id as TLShapeId)
            .map((id) => editor.getShape(id))
            .filter(Boolean);

          // If any child has opacity > 0, it's likely in edit mode
          if (children.some((child: any) => child.opacity > 0)) {
            maskGroup = group;
            break;
          }
        }
      }
    }

    if (!maskGroup) {
      return;
    }

    const groupId = maskGroup.id;

    // Delete ALL preview shapes (not just for this group, to avoid orphans)
    const previewShapes = allShapes.filter((s) => s.meta?.isPreviewShape);
    if (previewShapes.length > 0) {
      // FIRST: Unlock previews (isLocked might prevent deletion)
      previewShapes.forEach((preview) => {
        editor.updateShape({
          ...preview,
          isLocked: false,
        });
      });

      // THEN: Delete them
      editor.deleteShapes(previewShapes.map((s) => s.id));
    }

    // Hide all shapes in the mask group
    const childIds = editor.getSortedChildIdsForParent(groupId);
    const children = childIds.map((id) => editor.getShape(id)).filter(Boolean) as TLShape[];

    children.forEach((child) => {
      if (child.meta?.isInMaskGroup) {
        editor.updateShape({
          ...child,
          opacity: 0, // Hide the shape
          isLocked: true, // Lock to prevent selection
        });
      }
    });

    // Get current operation
    const operation = (maskGroup.meta?.maskOperation as string) || "union";
    const currentShapes = children.filter((s) => s.meta?.isInMaskGroup) as TLShape[];

    const label = getOperationLabel(operation);
    const booleanIndex =
      (maskGroup.meta?.booleanIndex as number | undefined) ??
      parseBooleanIndexFromName(maskGroup.meta?.name as string | undefined, label) ??
      getNextBooleanIndex(label, editor);
    const resultName = `${label} ${booleanIndex}`;
    const groupName = resultName; // group named same as result shape

    if (currentShapes.length >= 2) {
      // Perform boolean operation
      const result = performBooleanOperation(
        currentShapes,
        operation as "union" | "subtract" | "intersect" | "exclude",
        editor
      );

      // Validate result
      if (!result || result.isEmpty) {
        // Even if boolean fails, we must exit edit mode properly
        editor.updateShape({
          id: groupId,
          type: "group",
          meta: {
            ...maskGroup.meta,
            inEditMode: false,
            booleanLabel: label,
            booleanIndex,
            name: groupName,
          },
          opacity: 0,
          isLocked: true,
        });

        return;
      }

      const firstShapeColor = getShapeColor(currentShapes[0]);

      // Check if any shape is an image
      const hasImageShape = currentShapes.some(isImageShape);
      const imageProps = hasImageShape ? getImageProperties(currentShapes, editor) : null;

      // Create NEW mask shape
      const newMaskShapeId = createShapeId();

      if (hasImageShape && imageProps) {
        // For image masks, create a single customImage (same as createMaskShape)
        const imageOffsetX = imageProps.originalX - result.bounds.minX;
        const imageOffsetY = imageProps.originalY - result.bounds.minY;

        // For union/exclude: scale image to fill entire result bounds (use as texture)
        // For intersect/subtract: keep original image dimensions (just clip)
        const shouldScaleImage = operation === "union" || operation === "exclude";

        // Calculate clip-path based on operation type
        let imageClipPath: string;

        if (shouldScaleImage) {
          // For union/exclude: clip-path relative to result bounds (image will scale to fill)
          const pathRegex = /([ML])\s*([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)/g;
          imageClipPath = result.svgPath.replace(pathRegex, (match, command, x, y) => {
            const absX = parseFloat(x);
            const absY = parseFloat(y);
            const relX = absX - result.bounds.minX; // Relative to result bounds
            const relY = absY - result.bounds.minY;
            return `${command} ${relX} ${relY}`;
          });
        } else {
          // For intersect/subtract: clip-path relative to original image position
          const pathRegex = /([ML])\s*([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)/g;
          imageClipPath = result.svgPath.replace(pathRegex, (match, command, x, y) => {
            const absX = parseFloat(x);
            const absY = parseFloat(y);
            const relX = absX - imageProps.originalX;
            const relY = absY - imageProps.originalY;
            return `${command} ${relX} ${relY}`;
          });
        }

        editor.createShape({
          id: newMaskShapeId,
          type: "customImage",
          parentId: editor.getCurrentPageId(), // Ensure shape is created on page, not inside group
          x: result.bounds.minX,
          y: result.bounds.minY,
          opacity: 1,
          props: {
            w: result.bounds.width,
            h: result.bounds.height,
            transparency: 1,
            rounded: 0,
            assetId: imageProps.assetId,
            url: imageProps.url,
            crop: imageProps.crop,
            flipX: imageProps.flipX || false,
            flipY: imageProps.flipY || false,
            playing: false,
            colorMix: {},
            outlineColorMix: {},
            outShadowArray: {},
            inShadowArray: {},
            isHiddenLayerBlur: true,
            isHiddenBorder: true,
            filterBlur: 0,
            borderWidth: 0,
            strokeWidth: 0,
            dash: "solid" as const,
            outlineColor: "transparent",
            fill: "transparent",
          } as any,
          meta: {
            isMaskShape: true,
            maskGroupId: groupId,
            maskOperation: operation,
            maskClipPath: imageClipPath,
            // Only store original dimensions for intersect/subtract
            // For union/exclude, omit these so image scales to fill the shape
            ...(shouldScaleImage
              ? {}
              : {
                  originalImageWidth: imageProps.originalW,
                  originalImageHeight: imageProps.originalH,
                  imageOffsetX: imageOffsetX,
                  imageOffsetY: imageOffsetY,
                }),
            isImageMask: true,
            name: resultName,
            booleanLabel: label,
            booleanIndex,
          },
        });
      } else {
        // Create as customRect (existing code)
        editor.createShape({
          id: newMaskShapeId,
          type: "customRect",
          parentId: editor.getCurrentPageId(), // Ensure shape is created on page, not inside group
          x: result.bounds.minX,
          y: result.bounds.minY,
          opacity: 1, // Visible
          props: {
            w: result.bounds.width,
            h: result.bounds.height,
            transparency: 1,
            rounded: 0,
            colorMix: {
              "0": { color: firstShapeColor || "#e3e3e3", isHidden: false },
            },
            outlineColorMix: {},
            outShadowArray: {},
            inShadowArray: {},
            isHiddenLayerBlur: true,
            isHiddenBorder: true,
            filterBlur: 0,
            borderWidth: 0,
            strokeWidth: 0,
            dash: "solid" as const,
            outlineColor: "transparent",
          },
          meta: {
            isMaskShape: true,
            maskGroupId: groupId,
            maskOperation: operation,
            inEditMode: false,
            name: resultName,
            booleanLabel: label,
            booleanIndex,
          },
        });
      }

      // Convert to relative clip-path (only for customRect, customImage already has it)
      if (!hasImageShape) {
        const createdMaskShape = editor.getShape(newMaskShapeId);
        if (createdMaskShape) {
          const relativeClipPath = calculateRelativeClipPath(result.svgPath, createdMaskShape, editor);
          editor.updateShape({
            id: newMaskShapeId,
            type: "customRect",
            meta: {
              ...createdMaskShape.meta,
              maskClipPath: relativeClipPath,
            },
          });
        }
      }

      // Update group to point to new mask shape
      editor.updateShape({
        id: groupId,
        type: "group",
        meta: {
          ...maskGroup.meta,
          maskShapeId: newMaskShapeId,
          inEditMode: false,
          booleanLabel: label,
          booleanIndex,
          name: groupName,
        },
      });

      // Select the new mask shape
      editor.select(newMaskShapeId as any);
    } else {
      // Still need to exit edit mode properly
      editor.updateShape({
        id: groupId,
        type: "group",
        meta: {
          ...maskGroup.meta,
          inEditMode: false,
          booleanLabel: label,
          booleanIndex,
          name: groupName,
        },
        opacity: 0,
        isLocked: true,
      });
    }
  } catch (error) {
    // Last resort: try to find ANY mask group in edit mode and force exit
    try {
      const allShapes = editor.getCurrentPageShapes();
      const anyMaskGroup = allShapes.find(
        (s) => s.type === "group" && s.meta?.isMaskGroup && s.meta?.inEditMode === true
      );

      if (anyMaskGroup) {
        // Hide all children
        const childIds = editor.getSortedChildIdsForParent(anyMaskGroup.id as TLShapeId);
        childIds.forEach((childId) => {
          const child = editor.getShape(childId);
          if (child && child.meta?.isInMaskGroup) {
            editor.updateShape({
              ...child,
              opacity: 0,
              isLocked: true,
            });
          }
        });

        // Mark group as not in edit mode
        editor.updateShape({
          id: anyMaskGroup.id,
          type: "group",
          meta: {
            ...anyMaskGroup.meta,
            inEditMode: false,
          },
          opacity: 0,
          isLocked: true,
        });
      }
    } catch (fallbackError) {
      // Error exiting mask edit mode
    }
  }
}
